import { inject, Injectable, signal } from '@angular/core';
import { ConfigService } from '@anduin/config';
import { buildActiveFilters } from '@anduin/shared';
import { injectPaymentsTracking } from './payments-tracking.providers';
import {
  PaymentPlansBaseMetadata,
  PaymentPlansStepMetadataInput,
  PaymentPlansSurface,
  PaymentPlansVersionLabel,
  PaymentsPlansStep,
} from './payment-plans-workflow.models';
import { PaymentsWorkflowStepMetaDataMap } from './payments-workflow.models';

/**
 * Per-container tracking facade for Payment Plans. Wraps `TrackWorkflowService` and stamps
 * `PaymentPlansBaseMetadata` (surface, planCountVisible, activeFilters, planVersion) onto
 * every event so call sites only pass step extras.
 *
 * The shared payment-plans table is mounted by two surfaces (`STANDALONE` Payment Plans
 * page, `CLIENT_DETAIL` plans tab). Each surface owns its own setter-driven state, so the
 * service is registered per-container (`providers: [PaymentPlansTrackService]`) — root
 * scope would let one surface's `_columnFilters` leak into the other on navigation.
 */
@Injectable()
export class PaymentPlansTrackService {
  private readonly tracker = injectPaymentsTracking();
  private readonly configService = inject(ConfigService);

  private readonly _surface = signal<PaymentPlansSurface>('STANDALONE');
  private readonly _planCountVisible = signal<number | null>(null);
  private readonly _searchValue = signal<string>('');
  private readonly _columnFilters = signal<Record<string, unknown>>({});

  setSurface(surface: PaymentPlansSurface): void {
    this._surface.set(surface);
  }

  setPlanCountVisible(count: number | null | undefined): void {
    this._planCountVisible.set(count ?? null);
  }

  setSearchValue(value: string | null | undefined): void {
    this._searchValue.set(value ?? '');
  }

  setColumnFilters(model: Record<string, unknown> | null | undefined): void {
    this._columnFilters.set(model ?? {});
  }

  trackWorkflow<E extends PaymentsPlansStep>(step: E, metadata?: PaymentPlansStepMetadataInput<E>): void {
    const merged = { ...this.resolveBaseMetadata(), ...(metadata ?? {}) } as PaymentsWorkflowStepMetaDataMap[E];
    this.tracker.trackWorkflow(step, merged);
  }

  private resolveBaseMetadata(): PaymentPlansBaseMetadata {
    return {
      surface: this._surface(),
      planCountVisible: this._planCountVisible(),
      activeFilters: this.resolveActiveFilters(),
      planVersion: this.resolvePlanVersion(),
    };
  }

  private resolveActiveFilters(): string {
    const tokens: string[] = [];
    for (const [columnId, model] of Object.entries(this._columnFilters())) {
      if (model != null && model !== '') {
        tokens.push(`column:${columnId}`);
      }
    }
    if (this._searchValue().trim().length > 0) {
      tokens.push('search');
    }
    return buildActiveFilters(tokens);
  }

  private resolvePlanVersion(): PaymentPlansVersionLabel {
    return this.configService.paymentPlanV4Enabled ? 'v4' : 'v3';
  }
}
